import MirrorBackButton from "@/components/MirrorBackButton";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Sparkles, ChevronLeft, ChevronRight, Trash2, Pencil,
  ArrowRight, Check, X, Lightbulb, Zap,
  Send, Mic,
} from "lucide-react";
import MobileLayout from "@/components/MobileLayout";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor, type Lang } from "@/i18n/locale";
import { InfoTooltip } from "@/components/InfoTooltip";
import { useNotes, snapshotForNote, type NoteType, type NoteSnapshot } from "@/hooks/useNotes";
import { useXP } from "@/hooks/useXP";
import { SourceBadge } from "@/components/SourceBadge";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { noteTitle } from "@/lib/noteTitle";
import { useLibraryIdentity } from "@/hooks/useLibraryIdentity";
import { NotePendingActionLauncher } from "@/components/mirror/NoteActionModal";
import { newNoteOperationId, type NoteRecord } from "@/lib/noteExecutor";
import { createNoteContextHandoff } from "@/lib/noteContextHandoff";
import { MAX_NOTE_CONTENT } from "../../supabase/functions/_shared/noteActionDomain";

// 21/05/26: "task" removed. Notes are pure thought capture. Anything to-do-
// shaped lives in the Agenda's `tasks` list. The Schedule affordance was
// also removed from Notes — events created via "Agendar uma nota" in the
// past keep existing in agenda_events (source='note') and are not touched.

const getTypeConfig = (t: (k: string) => string) => ({
  idea: {
    label: t("notes.type.idea"), icon: Lightbulb,
    bg: "hsl(265 60% 55% / 0.14)", text: "hsl(265 70% 75%)", border: "hsl(265 60% 55% / 0.35)", glow: "hsl(265 60% 55% / 0.20)",
  },
  insight: {
    label: t("notes.type.insight"), icon: Zap,
    bg: "hsl(187 72% 50% / 0.12)", text: "hsl(187 72% 65%)", border: "hsl(187 72% 50% / 0.30)", glow: "hsl(187 72% 50% / 0.18)",
  },
});

const NOTE_TYPES: NoteType[] = ["idea", "insight"];

const formatNoteDate = (iso: string, lang: Lang, t: (k: string) => string): string => {
  const locale = localeTagFor(lang);
  const date = new Date(iso);
  const time = date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  const isToday = date.toDateString() === new Date().toDateString();
  const isYesterday = date.toDateString() === new Date(Date.now() - 86400000).toDateString();
  if (isToday) return `${t("notes.today")}, ${time}`;
  if (isYesterday) return `${t("notes.yesterday")}, ${time}`;
  return date.toLocaleDateString(locale, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

// ── Particles & Glow Orbs ───────────────────────────────────────────────────

const PARTICLE_CONFIGS = Array.from({ length: 20 }, (_, i) => ({
  w: Math.random() * 2 + 1,
  color: i % 3 === 0 ? "#00e5ff" : i % 3 === 1 ? "hsl(265 60% 65%)" : "hsl(220 70% 65%)",
  opacity: Math.random() * 0.18 + 0.06,
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  yDrift: -(Math.random() * 30 + 10),
  xDrift: Math.random() * 20 - 10,
  dur: Math.random() * 8 + 6,
  delay: Math.random() * 5,
}));

const Particles = React.memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
    {PARTICLE_CONFIGS.map((p, i) => (
      <motion.div key={i} className="absolute rounded-full"
        style={{ width: p.w, height: p.w, background: p.color, opacity: p.opacity, left: p.left, top: p.top }}
        animate={{ y: [0, p.yDrift, 0], x: [0, p.xDrift, 0], opacity: [p.opacity, p.opacity + 0.15, p.opacity] }}
        transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: p.delay }} />
    ))}
  </div>
));
Particles.displayName = "Particles";

const GlowOrbs = React.memo(() => (
  <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
    <motion.div className="absolute w-[300px] h-[300px] rounded-full"
      style={{ top: "0%", left: "-10%", background: "radial-gradient(circle, hsl(265 60% 25% / 0.22), transparent 70%)" }}
      animate={{ opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }} />
    <motion.div className="absolute w-[240px] h-[240px] rounded-full"
      style={{ top: "40%", right: "-12%", background: "radial-gradient(circle, hsl(187 72% 50% / 0.14), transparent 70%)" }}
      animate={{ opacity: [0.2, 0.4, 0.2] }} transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 3 }} />
    <motion.div className="absolute w-[180px] h-[180px] rounded-full"
      style={{ bottom: "20%", left: "20%", background: "radial-gradient(circle, hsl(220 70% 30% / 0.14), transparent 70%)" }}
      animate={{ opacity: [0.15, 0.30, 0.15] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 5 }} />
  </div>
));
GlowOrbs.displayName = "GlowOrbs";

// ── Main Component ──────────────────────────────────────────────────────────

const Notes = () => {
  const identity = useLibraryIdentity();
  const { t } = useLanguage();
  if (!identity.ready) return <MobileLayout><p role="status">{t("common.loading")}</p></MobileLayout>;
  if (!identity.userId || identity.error) return <MobileLayout><p role="alert">{t("mirror.connector.accountChanged")}</p><button onClick={identity.retry}>{t("common.retry")}</button></MobileLayout>;
  return <NotesContent key={identity.userId + ":" + identity.generation} ownerId={identity.userId} assertIdentity={identity.assertCurrent} />;
};
const NotesContent = ({ ownerId, assertIdentity }: { ownerId: string; assertIdentity: () => Promise<void> }) => {
  const navigate = useNavigate();
  const { t, tf, lang } = useLanguage();
  const {
    notes, loading, loadError, saving, refresh,
    addNote, updateNote, deleteNote,
    markSentToMirror, userId, hasMore, loadMore,
  } = useNotes();
  const { addXP, totalXP, level, getLevelProgress } = useXP();

  const [view, setView] = useState<"list" | "detail">("list");
  const [filter, setFilter] = useState<"all" | NoteType>("all");
  const [search, setSearch] = useState("");
  const [captureText, setCaptureText] = useState("");
  const [captureType, setCaptureType] = useState<NoteType>("idea");
  const [detailNote, setDetailNote] = useState<ReturnType<typeof useNotes>["notes"][0] | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(false);
  const [reviewChanged, setReviewChanged] = useState(false);
  const editSnapshot = useRef<NoteSnapshot | null>(null), deleteSnapshot = useRef<NoteSnapshot | null>(null);
  const captureId = useRef(newNoteOperationId()), captureSource = useRef<"manual" | "voice">("manual");
  const epoch = useRef(0), timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => { const lifecycle = epoch, pendingTimers = timers; return () => { lifecycle.current++; for (const timer of pendingTimers.current) clearTimeout(timer); }; }, []);
  const currentGuard = () => { const generation = epoch.current; return () => { if (generation !== epoch.current || userId !== ownerId) throw new Error("invalid_session"); }; };
  const changed = (cause: unknown) => cause instanceof Error && /changed|stale|snapshot|conflict/.test(cause.message);
  const showFailure = (cause: unknown) => { setReviewChanged(changed(cause)); toast.error(t(changed(cause) ? "noteAction.changed" : "noteAction.error")); };
  const editSavingRef = useRef(false);
  const captureSavingRef = useRef(false);
  const [justAdded, setJustAdded] = useState<string | null>(null);

  // Two-step delete: first tap arms (toast + red icon), second tap within 3s
  // executes. Auto-disarms after 3s. noteId being confirmed; null when idle.
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const confirmDeleteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const captureRef = useRef<HTMLTextAreaElement>(null);
  const typeConfig = getTypeConfig(t);
  const voice = useVoiceInput(lang);

  useEffect(() => { const stop = voice.stopListening; return () => { if (typeof stop === "function") stop(); }; }, [voice.stopListening]);

  useEffect(() => {
    if (voice.transcript) { setCaptureText(voice.transcript); captureSource.current = "voice"; }
  }, [voice.transcript]);

  useEffect(() => {
    if (voice.error) toast.error(t("notes.voice.error"));
  }, [voice.error, t]);

  const normalizeSearch = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const query = normalizeSearch(search.trim());
  const filteredNotes = notes.filter(n => n.user_id === ownerId &&
    (filter === "all" || n.type === filter) && normalizeSearch(n.content).includes(query));
  const hasFilters = search.trim().length > 0 || filter !== "all";

  const handleCapture = async () => {
    if (captureSavingRef.current || saving || captureText.trim().length < 2 || captureText.length > MAX_NOTE_CONTENT) return;
    const assert = currentGuard(), content = captureText.trim(), approvedType = captureType;
    captureSavingRef.current = true;
    try {
      assert(); await assertIdentity(); assert();
      const note = await addNote(content, approvedType, { id: captureId.current, source: captureSource.current }); assert();
      if (!note || note.user_id !== ownerId) throw new Error("note_result_unconfirmed");
      captureId.current = newNoteOperationId(); captureSource.current = "manual"; setCaptureText(""); setJustAdded(note.id);
      timers.current.push(setTimeout(() => { try { assert(); setJustAdded(null); } catch { /* A different account owns this view. */ } }, 1500));
      captureRef.current?.focus();
      const xpAmount = approvedType === "insight" ? 8 : 6;
      await assertIdentity(); assert();
      const { leveledUp, newLevel, inserted } = await addXP("notes", `note_${approvedType}`, xpAmount, note.id, { ownerId, isCurrent: () => { try { assert(); return true; } catch { return false; } } }); assert(); await assertIdentity(); assert();
      if (!inserted) return;
      toast.custom(() => <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-card border border-border"><Sparkles size={13} /><span>+{xpAmount} XP — {typeConfig[approvedType].label}</span></div>, { duration: 1500 });
      if (leveledUp) timers.current.push(setTimeout(() => { try { assert(); toast.custom(() => <div className="rounded-xl bg-card p-3">⭐ {t("habits.xp.levelUp")} {newLevel}</div>, { duration: 3500 }); } catch { /* The previous owner's reward stays private. */ } }, 600));
    } catch (cause) { try { assert(); showFailure(cause); } catch { /* Leave the old owner's original journal recoverable. */ } }
    finally { try { assert(); captureSavingRef.current = false; } catch { /* This form has been replaced. */ } }
  };

  const handleSendToMirror = async (note: NoteRecord) => {
    if (editSavingRef.current || saving || reviewChanged) return;
    const snapshot = snapshotForNote(note), assert = currentGuard(); if (!snapshot) { showFailure(new Error("incomplete_note_snapshot")); return; }
    editSavingRef.current = true; setEditSaving(true);
    try {
      assert(); await assertIdentity(); assert();
      const confirmed = await markSentToMirror(note.id, snapshot); assert(); await assertIdentity(); assert();
      const handoff = createNoteContextHandoff(confirmed, ownerId); if (!handoff) throw new Error("invalid_session");
      navigate("/mirror", { state: { noteContext: handoff } });
    } catch (cause) { try { assert(); showFailure(cause); } catch { /* Never navigate with a previous account's note. */ } }
    finally { try { assert(); editSavingRef.current = false; setEditSaving(false); } catch { /* The new owner has another view. */ } }
  };
  const handleEditSave = async () => {
    if (!detailNote || !editContent.trim() || editContent.length > MAX_NOTE_CONTENT || editSavingRef.current || reviewChanged) return;
    const snapshot = editSnapshot.current, assert = currentGuard(), id = detailNote.id, content = editContent.trim();
    if (!snapshot) { showFailure(new Error("incomplete_note_snapshot")); return; }
    editSavingRef.current = true; setEditSaving(true); setEditError(false);
    try {
      assert(); await assertIdentity(); assert();
      const updated = await updateNote(id, content, snapshot); assert();
      if (!updated || updated.id !== id || updated.user_id !== ownerId) throw new Error("note_result_unconfirmed");
      setDetailNote(updated); editSnapshot.current = snapshotForNote(updated); setIsEditing(false); toast.success(t("notes.updated"));
    } catch (cause) { try { assert(); setEditError(true); showFailure(cause); } catch { /* An old form cannot publish into a new account. */ } }
    finally { try { assert(); editSavingRef.current = false; setEditSaving(false); } catch { /* The original journal remains owned. */ } }
  };
  const refreshReview = async () => {
    const assert = currentGuard();
    try { assert(); await assertIdentity(); assert(); await refresh(); assert(); setIsEditing(false); setDetailNote(null); setView("list"); setReviewChanged(false); setEditError(false); setConfirmDelete(null); deleteSnapshot.current = null; }
    catch (cause) { try { assert(); showFailure(cause); } catch { /* The account changed during refresh. */ } }
  };
  const handleDelete = async (note: NoteRecord) => {
    if (editSavingRef.current || saving || reviewChanged) return;
    const assert = currentGuard();
    if (confirmDelete === note.id) {
      const snapshot = deleteSnapshot.current; if (!snapshot) { showFailure(new Error("incomplete_note_snapshot")); return; }
      if (confirmDeleteTimeoutRef.current) clearTimeout(confirmDeleteTimeoutRef.current);
      setConfirmDelete(null); editSavingRef.current = true; setEditSaving(true);
      try { assert(); await assertIdentity(); assert(); await deleteNote(note.id, snapshot); assert(); toast.success(t("notes.deleted")); setView("list"); setDetailNote(null); }
      catch (cause) { try { assert(); showFailure(cause); } catch { /* An old deletion cannot publish to another account. */ } }
      finally { try { assert(); editSavingRef.current = false; setEditSaving(false); } catch { /* The previous owner's journal is retained. */ } }
      return;
    }
    deleteSnapshot.current = snapshotForNote(note); if (!deleteSnapshot.current) { showFailure(new Error("incomplete_note_snapshot")); return; }
    if (confirmDeleteTimeoutRef.current) clearTimeout(confirmDeleteTimeoutRef.current);
    setConfirmDelete(note.id); toast(t("notes.delete.confirm.toast"), { duration: 3000 });
    confirmDeleteTimeoutRef.current = setTimeout(() => { try { assert(); setConfirmDelete(null); deleteSnapshot.current = null; } catch { /* This confirmation is no longer current. */ } }, 3000);
    timers.current.push(confirmDeleteTimeoutRef.current);
  };

  const renderList = () => (
    <motion.div key="list" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }} className="space-y-5 relative z-10">
      <MirrorBackButton />

      <div>
        <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5" style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("notes.subtitle")}</p>
        <div className="flex items-center gap-2">
          <h1 className="font-display text-[28px] font-semibold text-foreground leading-none">{t("notes.title")}</h1>
          <InfoTooltip title={t("tooltips.notes.title")} description={t("tooltips.notes.description")} />
        </div>
      </div>

      <NotePendingActionLauncher ownerId={ownerId} busy={saving || editSaving} onMirrorResume={locator => navigate("/mirror", { state: { noteActionRecovery: locator } })} />
      {/* XP Bar */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.4 }}
        className="flex items-center gap-3 rounded-xl px-3 py-2.5"
        style={{ background: "linear-gradient(135deg, hsl(265 60% 12%), hsl(220 35% 10%))", border: "1px solid hsl(265 60% 55% / 0.20)" }}>
        <span className="px-2.5 py-1 rounded-full font-display text-[10px] font-bold shrink-0"
          style={{ background: "hsl(265 60% 55% / 0.15)", border: "1px solid hsl(265 60% 55% / 0.35)", color: "hsl(265 70% 72%)" }}>
          {tf("habits.xp.level", { level: String(level) })}
        </span>
        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "hsl(218 14% 18%)" }}>
          <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${getLevelProgress()}%` }}
            transition={{ delay: 0.4, duration: 0.9, ease: "easeOut" }}
            style={{ background: "linear-gradient(90deg, hsl(265 60% 45%), hsl(265 70% 65%))", boxShadow: "0 0 8px hsl(265 60% 55% / 0.40)" }} />
        </div>
        <span className="text-[9px] font-display shrink-0" style={{ color: "hsl(218 14% 55%)" }}>{totalXP} XP</span>
        <motion.div animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.2, 1] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}>
          <Sparkles size={12} style={{ color: "hsl(265 70% 65%)" }} />
        </motion.div>
      </motion.div>

      {/* Capture: type choices are visible, with native keyboard selection. */}
      <form onSubmit={e => { e.preventDefault(); void handleCapture(); }}
        className="rounded-2xl border p-4 space-y-4"
        style={{ background: "linear-gradient(135deg, hsl(220 35% 12%), hsl(225 30% 8%))", borderColor: typeConfig[captureType].border }}>
        <fieldset disabled={saving}>
          <legend className="mb-2 text-sm font-medium text-foreground">{t("ux.notes.typeLabel")}</legend>
          <div className="grid grid-cols-2 gap-2">
            {NOTE_TYPES.map(type => {
              const cfg = typeConfig[type]; const Icon = cfg.icon;
              return (
                <label key={type} className="relative min-w-0 cursor-pointer">
                  <input type="radio" name="note-type" value={type} checked={captureType === type}
                    onChange={() => setCaptureType(type)} className="peer sr-only" />
                  <span className="block h-full rounded-xl border p-3 peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-disabled:opacity-50"
                    style={{ background: captureType === type ? cfg.bg : "hsl(220 35% 10%)",
                      borderColor: captureType === type ? cfg.border : "hsl(var(--border))" }}>
                    <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <Icon size={16} style={{ color: cfg.text }} aria-hidden="true" />
                      {cfg.label}
                      {captureType === type && <Check size={14} className="ml-auto shrink-0" aria-hidden="true" />}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{t(type === "idea" ? "ux.notes.ideaHint" : "ux.notes.insightHint")}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
        <div>
          <label htmlFor="note-capture" className="mb-2 block text-sm font-medium text-foreground">{t("ux.notes.newNote")}</label>
          <textarea id="note-capture" ref={captureRef} rows={3} disabled={saving}
            aria-describedby="note-title-hint"
            value={captureText} onChange={e => { setCaptureText(e.target.value); captureSource.current = "manual"; }}
            onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !e.nativeEvent.isComposing) { e.preventDefault(); void handleCapture(); } }}
            placeholder={t("notes.placeholder")}
            className="block w-full resize-y rounded-xl border border-border bg-background/50 p-3 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50" />
          {captureText.length > MAX_NOTE_CONTENT && <p role="alert">{t("noteAction.contentTooLong")}</p>}
          <p id="note-title-hint" className="mt-2 text-xs text-muted-foreground">{t("ux.notes.titleHint")}</p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          {voice.supported && <button type="button" disabled={saving}
            aria-pressed={voice.isListening}
            onClick={() => { if (voice.isListening) { const text = voice.stopListening(); if (text) { setCaptureText(text); captureSource.current = "voice"; } } else { voice.startListening(); } }}
            className="flex min-h-11 items-center gap-2 rounded-xl border border-border px-3 text-sm text-foreground focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40">
            <Mic size={16} aria-hidden="true" />{t(voice.isListening ? "ux.notes.stopDictation" : "ux.notes.dictate")}
          </button>}
          <button type="submit" disabled={saving || userId !== ownerId || captureText.trim().length < 2 || captureText.length > MAX_NOTE_CONTENT}
            className="ml-auto flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-40">
            <Send size={16} aria-hidden="true" />{t(saving ? "ux.notes.saving" : "ux.notes.saveNote")}
          </button>
        </div>
      </form>

      {/* Filter pills — 3 now: All · Idea · Insight. */}
      <div>
        <label htmlFor="notes-search" className="mb-2 block text-sm text-foreground">{t("ux.notes.search")}</label>
        <input id="notes-search" type="search" value={search} onChange={e => setSearch(e.target.value)}
          aria-describedby="notes-search-scope"
          className="min-h-11 w-full rounded-xl border border-border bg-card px-3 text-base text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" />
        <p id="notes-search-scope" className="mt-2 text-xs text-muted-foreground">{t("ux.notes.searchScope")}</p>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {(["all", ...NOTE_TYPES] as const).map(f => {
          const isActive = filter === f;
          const cfg = f !== "all" ? typeConfig[f] : null;
          return (
            <button key={f} onClick={() => setFilter(f)} aria-pressed={isActive}
              className="min-h-11 px-4 py-1.5 rounded-full font-display text-sm tracking-wide transition-all duration-200 active:scale-95 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              style={
                isActive && cfg
                  ? { background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.text, fontWeight: 600 }
                  : isActive
                  ? { background: "hsl(187 72% 50% / 0.15)", border: "1px solid hsl(187 72% 50% / 0.30)", color: "hsl(187 72% 60%)", fontWeight: 600 }
                  : { background: "hsl(187 72% 50% / 0.07)", border: "1px solid hsl(187 72% 50% / 0.15)", color: "hsl(210 20% 55%)" }
              }
            >
              {f === "all" ? t("notes.filter.all") : typeConfig[f].label}
            </button>
          );
        })}
      </div>

      {/* Notes list */}
      {loadError && <div className="rounded-xl border border-amber-400/30 p-4">
        <p role="alert" className="text-sm text-foreground">{t(notes.length ? "ux.notes.refreshError" : "ux.notes.loadError")}</p>
        <button onClick={refresh} disabled={loading} className="mt-2 min-h-11 rounded-lg px-3 text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">{t("ux.notes.retry")}</button>
      </div>}
      {loading && <p role="status" className="text-sm text-muted-foreground">{t("ux.notes.loading")}</p>}
      {loading && notes.length === 0 ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 rounded-2xl animate-pulse" style={{ background: "hsl(220 35% 12%)" }} />)}</div>
      ) : filteredNotes.length === 0 && !loadError ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="text-[40px] mb-4">{"\u{1F4AD}"}</div>
          <p role="status" className="font-display text-sm text-foreground">{t(hasFilters ? "ux.notes.noResults" : "notes.empty")}</p>
          {hasFilters ? <button onClick={() => { setSearch(""); setFilter("all"); }} className="mt-2 min-h-11 rounded-lg px-3 text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">{t("ux.notes.clearFilters")}</button>
            : <p className="text-sm text-muted-foreground mt-1">{t("notes.emptySubtitle")}</p>}
        </div>
      ) : (
        <ul className="space-y-2" aria-label={t("ux.notes.savedNotes")}>
          {filteredNotes.map(note => {
            const cfg = typeConfig[note.type as NoteType] ?? typeConfig.idea;
            const title = noteTitle(note.content, t("ux.notes.untitled"));
            return (
              <li key={note.id} className="min-w-0">
                <button type="button"
                  className="block w-full min-w-0 rounded-xl border px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  style={{ background: "hsl(220 35% 10%)", borderColor: justAdded === note.id ? cfg.border : "hsl(var(--border))" }}
                  onClick={() => { setDetailNote(note); setEditContent(note.content); setIsEditing(false); setEditError(false); setReviewChanged(false); setConfirmDelete(null); setView("detail"); }}>
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="min-w-0 flex-1 text-sm font-medium text-foreground"
                      style={{ display: "block", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{title}</span>
                    <span className="sr-only">{t("ux.notes.open")}</span>
                    <ChevronRight size={18} className="shrink-0 text-muted-foreground" aria-hidden="true" />
                  </span>
                  <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>{formatNoteDate(note.created_at, lang, t)}</span>
                    <span style={{ color: cfg.text }}>{note.type === "task" ? t("noteAction.legacyType") : cfg.label}</span>
                    <SourceBadge source={note.source as React.ComponentProps<typeof SourceBadge>["source"]} size="xs" />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {hasMore && <button disabled={loading} onClick={() => void loadMore()} className="min-h-11 rounded-xl border border-border px-4">{t("noteAction.loadMore")}</button>}
      {/* Mirror CTA */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
        className="rounded-2xl p-4 relative overflow-hidden"
        style={{ background: "linear-gradient(rgba(0,0,0,0.52), rgba(0,0,0,0.68)), url('https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_05.jpeg') center/cover no-repeat", border: "1px solid hsl(187 72% 50% / 0.25)", boxShadow: "0 0 30px hsl(187 72% 50% / 0.08)" }}>
        <motion.div className="absolute top-0 left-0 right-0 h-[1px]"
          animate={{ backgroundPosition: ["0% 50%", "200% 50%"] }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
          style={{ background: "linear-gradient(90deg, transparent, hsl(187 72% 50% / 0.70), hsl(265 60% 70% / 0.50), hsl(187 72% 50% / 0.70), transparent)", backgroundSize: "200% 100%" }} />
        <div className="flex items-start gap-3 relative z-10">
          <motion.div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            animate={{ scale: [1, 1.1, 1], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            style={{ background: "hsl(187 72% 50% / 0.15)", border: "1px solid hsl(187 72% 50% / 0.30)", boxShadow: "0 0 12px hsl(187 72% 50% / 0.20)" }}>
            <Sparkles size={13} style={{ color: "hsl(187 72% 60%)" }} />
          </motion.div>
          <div className="flex-1">
            <p className="text-[12px] font-body leading-relaxed mb-2" style={{ color: "hsl(218 14% 72%)" }}>{t("notes.mirrorCta")}</p>
            <button onClick={() => navigate("/mirror")} className="flex items-center gap-1.5 font-display text-[11px] font-medium py-2" style={{ color: "hsl(187 72% 60%)" }}>
              <span>{t("notes.openMirror")}</span><ArrowRight size={10} />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  const renderDetail = () => {
    if (!detailNote) return null;
    const cfg = typeConfig[detailNote.type as NoteType] ?? typeConfig.idea;
    const Icon = cfg.icon;
    return (
      <motion.div key="detail" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }} className="space-y-5 relative z-10">
        <div className="flex items-center justify-between">
          <button disabled={editSaving} aria-label={t("common.back")} onClick={() => { setView("list"); setIsEditing(false); }}
            className="min-w-11 min-h-11 rounded-xl bg-card/50 border border-border/20 flex items-center justify-center active:scale-95 transition-transform focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40">
            <ChevronLeft size={16} className="text-muted-foreground" />
          </button>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <button aria-label={t("ux.notes.edit")} disabled={editSaving || saving || !detailNote.writable || reviewChanged} onClick={() => { editSnapshot.current = snapshotForNote(detailNote); if (!editSnapshot.current) { showFailure(new Error("incomplete_note_snapshot")); return; } setEditContent(detailNote.content); setEditError(false); setIsEditing(true); }}
                className="min-w-11 min-h-11 rounded-xl bg-card/50 border border-border/20 flex items-center justify-center active:scale-95 transition-transform focus-visible:ring-2 focus-visible:ring-primary">
                <Pencil size={13} className="text-muted-foreground/60" />
              </button>
            ) : (
              <>
                <button disabled={editSaving} aria-label={t("common.cancel")} onClick={() => { setIsEditing(false); setEditError(false); }}
                  className="min-w-11 min-h-11 rounded-xl bg-card/50 border border-border/20 flex items-center justify-center active:scale-95 focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40">
                  <X size={13} className="text-muted-foreground/60" />
                </button>
                <button onClick={handleEditSave} disabled={editSaving || !editContent.trim() || editContent.length > MAX_NOTE_CONTENT || reviewChanged} aria-label={t("common.save")}
                  className="min-h-11 px-3 gap-2 rounded-xl flex items-center justify-center active:scale-95 focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
                  style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.30)" }}>
                  <Check size={14} className="text-primary/80" />
                  <span className="text-sm text-primary">{t("common.save")}</span>
                </button>
              </>
            )}
            {(() => {
              const armed = confirmDelete === detailNote.id;
              return (
                <button
                  disabled={editSaving || saving || !detailNote.writable || reviewChanged}
                  onClick={() => void handleDelete(detailNote)}
                  aria-label={armed ? t("notes.delete.confirm.toast") : t("common.delete")}
                  className={
                    "min-w-11 min-h-11 rounded-xl border flex items-center justify-center active:scale-95 transition-colors focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 " +
                    (armed
                      ? "bg-red-500/25 border-red-500/60"
                      : "bg-red-500/8 border-red-500/20")
                  }
                >
                  <Trash2 size={14} className={armed ? "text-red-400" : "text-red-400/60"} />
                </button>
              );
            })()}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-[9px] font-display tracking-[0.14em] uppercase px-2.5 py-1 rounded-full font-semibold"
            style={{ background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}` }}>
            <Icon size={9} />{detailNote.type === "task" ? t("noteAction.legacyType") : cfg.label}
          </span>
          <span className="text-[11px] font-body" style={{ color: "hsl(218 14% 55%)" }}>{formatNoteDate(detailNote.created_at, lang, t)}</span>
        </div>

        {detailNote.local_copy && <p className="text-sm text-muted-foreground">{t("noteAction.localCopy")}</p>}
        {!detailNote.writable && <p role="status">{t("noteAction.readOnly")}</p>}
        {reviewChanged && <div><p role="alert">{t("noteAction.changed")}</p><button className="min-h-11 rounded-xl border border-border px-3" disabled={editSaving || loading} onClick={() => void refreshReview()}>{t("noteAction.refreshReview")}</button></div>}
        {editContent.length > MAX_NOTE_CONTENT && isEditing && <p role="alert">{t("noteAction.contentTooLong")}</p>}
        {editSaving && <p role="status" className="text-sm text-muted-foreground">{t("ux.notes.saving")}</p>}
        {editError && <p role="alert" className="rounded-xl border border-amber-400/30 p-3 text-sm text-foreground">{t("ux.notes.editError")}</p>}
        <AnimatePresence mode="wait">
          {isEditing ? (
            <motion.div key="editing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="rounded-2xl p-4"
                style={{ background: "linear-gradient(145deg, hsl(220 35% 10%), hsl(225 30% 8%))", border: `1px solid ${cfg.border}`, boxShadow: `0 0 16px ${cfg.glow}`, minHeight: "160px" }}>
                <textarea autoFocus disabled={editSaving} aria-label={t("ux.notes.content")} value={editContent} onChange={e => setEditContent(e.target.value)}
                  className="w-full bg-transparent text-foreground/80 text-[14px] font-body leading-relaxed resize-none outline-none" style={{ minHeight: "140px" }} />
              </div>
            </motion.div>
          ) : (
            <motion.div key="reading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="rounded-2xl p-5"
                style={{ background: "linear-gradient(145deg, hsl(220 35% 10%), hsl(225 30% 8%))", border: `1px solid ${cfg.border}`, boxShadow: `0 0 20px ${cfg.glow}, inset 0 0 30px ${cfg.bg}` }}>
                <p className="text-foreground/75 text-[14px] font-body leading-relaxed whitespace-pre-wrap">{detailNote.content}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="space-y-2.5">
          <button onClick={() => void handleSendToMirror(detailNote)} disabled={isEditing || editSaving || saving || !detailNote.writable || reviewChanged}
            className="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl transition-all duration-200 active:scale-[0.98] disabled:opacity-40"
            style={{ background: "hsl(187 72% 50% / 0.08)", border: "1px solid hsl(187 72% 50% / 0.25)" }}>
            <div className="flex items-center gap-2.5">
              <Sparkles size={14} className="text-primary/70" />
              <span className="font-display text-[13px] font-medium text-primary/75">
                {detailNote.is_sent_to_mirror ? t("noteAction.sendAgain") : t("notes.sendToMirror")}
              </span>
            </div>
            {!detailNote.is_sent_to_mirror && <ArrowRight size={13} className="text-primary/50" />}
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <MobileLayout key={lang} className="pb-24 pt-8 relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #0a0a0f 0%, #0d1117 100%)" }}>
      <style>{`
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        @keyframes captureGlow {
          0%, 100% { box-shadow: 0 0 12px hsl(187 72% 50% / 0.08), inset 0 1px 0 hsl(187 72% 50% / 0.05); border-color: hsl(187 72% 50% / 0.25); }
          50% { box-shadow: 0 0 24px hsl(187 72% 50% / 0.18), inset 0 1px 0 hsl(187 72% 50% / 0.10); border-color: hsl(187 72% 50% / 0.50); }
        }
      `}</style>
      <Particles />
      <GlowOrbs />
      <AnimatePresence mode="wait">
        {view === "list" && renderList()}
        {view === "detail" && renderDetail()}
      </AnimatePresence>

    </MobileLayout>
  );
};

export default Notes;
